import { apiService } from '../../../shared/services/apiServices';
import { AxiosError } from 'axios';
import type {
  AddTechnicianDetails,
  MediaResource,
} from '../types/addTechnicianType';
import { TECHNICIAN_ERROR_MESSAGES } from '../errors';
import technicianEndpoints from './technicianEndpoints';

export interface AddTechnicianResult {
  success: boolean;
  data?: unknown;
  error?: string;
}

interface CreateTechnicianResponse {
  technician: {
    id: string;
    technicianId: string;
  };
}

const ERROR_MESSAGES = TECHNICIAN_ERROR_MESSAGES;

const extractErrorMessage = (errorData: unknown): string => {
  if (!errorData) {
    return ERROR_MESSAGES.DEFAULT_ERROR;
  }

  if (typeof errorData === 'string') {
    if (errorData.toLowerCase().includes('email already exists')) {
      return ERROR_MESSAGES.EMAIL_ALREADY_EXISTS;
    }
    return errorData;
  }

  if (typeof errorData === 'object') {
    const apiError = errorData as {
      message?: string;
      error?: string;
      details?: Array<{
        path?: string;
        message?: string;
        code?: string;
      }>;
    };

    // Handle validation errors with details array
    if (apiError.error === 'Input Validation Failed' && apiError.details) {
      const filenameError = apiError.details.find(
        (detail) =>
          detail.path === 'profile.filename' &&
          detail.message === 'Invalid characters in filename'
      );
      if (filenameError) {
        return ERROR_MESSAGES.INVALID_FILENAME;
      }
    }

    const errorMessage = apiError.message || apiError.error;

    // Replace generic server errors with user-friendly message
    if (errorMessage === 'Internal Server Error') {
      return ERROR_MESSAGES.UNEXPECTED_ERROR;
    }

    if (errorMessage?.toLowerCase().includes('email already exists')) {
      return ERROR_MESSAGES.EMAIL_ALREADY_EXISTS;
    }
    return errorMessage || ERROR_MESSAGES.DEFAULT_ERROR;
  }

  return ERROR_MESSAGES.DEFAULT_ERROR;
};

export const addTechnician = async (
  technicianDetails: AddTechnicianDetails,
  entityId?: string,
  profile?: MediaResource,
  docs?: MediaResource[]
): Promise<AddTechnicianResult> => {
  try {
    const requestBody: {
      id?: string;
      name: string;
      email: string;
      phoneNumber: string;
      countryCode: string;
      addressLine1: string;
      addressLine2: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
      role: string;
      privileges: typeof technicianDetails.privileges;
      profile?: MediaResource;
      docs?: MediaResource[];
    } = {
      name: technicianDetails.name.trim(),
      email: technicianDetails.email.trim().toLowerCase(),
      phoneNumber: technicianDetails.phone.trim(),
      countryCode: technicianDetails.countryCode.trim(),
      addressLine1: technicianDetails.address1.trim(),
      addressLine2: technicianDetails.address2.trim(),
      city: technicianDetails.city.trim(),
      state: technicianDetails.state.trim(),
      zipCode: technicianDetails.zip.trim(),
      country: technicianDetails.country.trim(),
      role: 'TECHNICIAN',
      privileges: technicianDetails.privileges,
    };

    // Add entityId if provided (from file upload flow)
    if (entityId) {
      requestBody.id = entityId;
    }

    // Add profile if provided
    if (profile) {
      requestBody.profile = profile;
    }

    // Add docs if provided
    if (docs && docs.length > 0) {
      requestBody.docs = docs;
    }

    const createResult = await apiService.postAPI(
      technicianEndpoints.TECHNICIANS,
      requestBody,
      { skipGlobalErrorRedirect: true }
    );

    if (createResult.error) {
      const errorMessage = extractErrorMessage(createResult.data);
      return {
        success: false,
        error: createResult.isNetworkError
          ? ERROR_MESSAGES.NETWORK_ERROR
          : errorMessage,
      };
    }

    const technicianResponse = createResult.data as CreateTechnicianResponse;

    return {
      success: true,
      data: technicianResponse,
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const isNetworkError = !error.response && !!error.request;
      if (isNetworkError) {
        return { success: false, error: ERROR_MESSAGES.NETWORK_ERROR };
      }
      const errorMessage = extractErrorMessage(error.response?.data);
      return { success: false, error: errorMessage };
    }

    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
